'use client';

import { useLocale, useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useState } from 'react';

import SectionTitle from '@/app/[locale]/(default-layout)/_components/SectionTitle';

import { ShowingInfoModal } from '@/components/showing-details/ShowingInfoModal';
import TicketLink, { useTicketUrl } from '@/components/ticket-shape/TicketLink';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import {
    getShowingPropertiesDimension,
    getShowingPropertiesLanguage,
    getShowingTechnologies
} from '@cineamo/ui/src/helpers/showing-helper';

import { CachedContentData } from '@/hooks/useCachedContentData';
import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';

import { IconInfo16 } from '@cineamo/ui/src/icons/ic-info';
import { IconTicket2_24 } from '@cineamo/ui/src/icons/ic-ticket';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { Link } from '@/i18n/routing';
import { useCinemaRooms } from '@/providers/CinemaRoomProvider';
import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';
import { useShowingTags } from '@/providers/ShowingTagProvider';

const MAX_VISIBLE_SHOWINGS = 6;

type ProgramTodayListItemProps = {
    showing: ShowingDto;
    content?: CachedContentData;
};

function ProgramTodayListItem(props: ProgramTodayListItemProps): React.JSX.Element {
    const { showing, content } = props;

    const t = useTranslations();
    const locale = useLocale();
    const domainConfiguration = useDomainConfiguration();
    const format = useFormat({ timeZone: domainConfiguration.timeZone });
    const { getCinemaRoomById } = useCinemaRooms();
    const { getShowingTagById } = useShowingTags();
    const { ticketUrl, hasTicketUrl, isExternal } = useTicketUrl(showing, content?.movieId);

    const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

    const time = format.dateTime.formatDate_HHmm24(showing.startDatetime);
    const cinemaRoom = getCinemaRoomById(showing.cinemaRoomId);
    const title = content?.title || showing.name;

    const showingTags = useMemo(() => {
        return showing.showingTagIds?.map((tagId) => getShowingTagById(tagId)).filter((tag) => !!tag) ?? [];
    }, [showing.showingTagIds, getShowingTagById]);

    const specialsLabel = showingTags
        .map((tag) => tag.name)
        .filter((label) => !!label)
        .join(', ');

    const infoLabels = useMemo(() => {
        const technologies = getShowingTechnologies(showing).map((technology) =>
            t(`showtimes.showing-info.technology.${technology}`)
        );
        return [
            cinemaRoom?.name,
            // The shared helper returns null for a 2D showing (the web app hides it); this website
            // labels it explicitly.
            [getShowingPropertiesDimension(showing) ?? '2D', ...technologies].join(' '),
            getShowingPropertiesLanguage(showing, locale).join(' ')
        ].filter((label) => !!label);
    }, [showing, cinemaRoom?.name, locale, t]);

    return (
        <div className="grid grid-cols-[52px_minmax(0,1fr)_auto] gap-x-12 gap-y-4">
            <div className="text-body-bold-16 tracking-[0.8px]">{time}</div>
            <div className="min-w-0 flex flex-col gap-y-4">
                {title && <div className="text-body-bold-16 [word-break:break-word]">{title}</div>}
                <div className="flex flex-row flex-wrap items-center gap-x-6 gap-y-2 text-body-12 font-montserrat-medium text-[rgba(var(--on-background-color-rgb),0.7)]">
                    {infoLabels.map((label, labelIndex) => (
                        <React.Fragment key={`ProgramTodayInfoLabel#${showing.id}-${labelIndex}`}>
                            {labelIndex > 0 && (
                                <span className="text-[rgba(var(--on-background-color-rgb),0.2)]">|</span>
                            )}
                            <span className="whitespace-pre">{label}</span>
                        </React.Fragment>
                    ))}
                    <button
                        type="button"
                        aria-label={t('showing-info.show-info')}
                        className="inline-flex cursor-pointer"
                        onClick={() => setIsInfoModalOpen(true)}>
                        <IconInfo16 className="w-14 h-14 opacity-60" />
                    </button>
                </div>
            </div>
            <TicketLink
                showing={showing}
                content={content}
                className="self-start justify-self-end">
                <span className="flex flex-row items-center gap-x-8 rounded-4 bg-[var(--primary-color)] px-12 py-6 text-body-bold-14 text-[var(--on-primary-color)]">
                    <IconTicket2_24 className="w-20 h-20 flex-none" />
                    <span>{t('tickets.tickets')}</span>
                </span>
            </TicketLink>
            {(specialsLabel || showing.highlightText) && (
                <div className="col-start-2 col-span-2 min-w-0 flex flex-col gap-y-4">
                    {specialsLabel && (
                        <div className="text-body-bold-12 text-[var(--primary-color)]">{specialsLabel}</div>
                    )}
                    {showing.highlightText && (
                        <div className="text-body-12 font-montserrat-medium text-[rgba(var(--on-background-color-rgb),0.7)]">
                            {showing.highlightText}
                        </div>
                    )}
                </div>
            )}
            {isInfoModalOpen && (
                <ShowingInfoModal
                    showing={showing}
                    content={content}
                    modalIsOpen={isInfoModalOpen}
                    setModalIsOpen={setIsInfoModalOpen}
                    specials={showingTags}
                    onClose={() => setIsInfoModalOpen(false)}
                    onShare={() => undefined}
                    onSubmit={() => {
                        if (hasTicketUrl) {
                            window.open(ticketUrl, isExternal ? '_blank' : '_self', 'noopener');
                        }
                    }}
                />
            )}
        </div>
    );
}

type ProgramTodayListProps = {
    showings: ShowingDto[];
    contents: CachedContentData[];
};

function ProgramTodayList(props: ProgramTodayListProps): React.JSX.Element | null {
    const t = useTranslations();

    const [now, setNow] = useState<number | null>(null);

    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, []);

    const upcomingShowings = useMemo(() => {
        return now
            ? props.showings.filter((showing) => new Date(showing.startDatetime).getTime() > now)
            : props.showings;
    }, [props.showings, now]);

    const visibleShowings = upcomingShowings.slice(0, MAX_VISIBLE_SHOWINGS);

    const contentById = useMemo(
        () => new Map(props.contents.map((content) => [content.id, content])),
        [props.contents]
    );

    if (visibleShowings.length === 0) {
        return null;
    }

    return (
        <CenterLayout className="flex flex-col gap-y-24">
            <SectionTitle title={t('home.program-today')} />
            <div className="-mb-24 [columns:1] lg:[columns:2] xl:[columns:3] [column-gap:48px] [column-rule:1px_solid_rgba(var(--on-background-color-rgb),0.2)]">
                {visibleShowings.map((showing) => (
                    <div
                        key={`ProgramTodayListItem#${showing.id}`}
                        className="break-inside-avoid pb-24">
                        <ProgramTodayListItem
                            showing={showing}
                            content={contentById.get(showing.contentId)}
                        />
                    </div>
                ))}
            </div>
            {upcomingShowings.length > MAX_VISIBLE_SHOWINGS && (
                <Link
                    href="/program"
                    className="text-body-bold-14 underline text-[var(--primary-color)]">
                    {t('specials.show-all')}
                </Link>
            )}
        </CenterLayout>
    );
}

export default ProgramTodayList;
